import React, {useState, useMemo, useEffect, forwardRef} from 'react';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';

import CurvedCircle from "../../icons/CurvedCircle";
import LoadingBlock from 'js/components/shared/LoadingBlock';
import keyCodes from 'js/shared/keyCodes';

import styles from './CollapsedSection.module.scss';

const cx = classnames.bind(styles);

const CollapsedSection = forwardRef((
  {
    className,
    children,
    title,
    open,
    loading,
    aria,
    titleClassName,
    onOpen,
    onClose,
    tag = 'div'
  },
  ref
) => {
  const [isOpen, setIsOpen] = useState(open);

  const Tag = tag;

  const toggleCollapse = function (action = !isOpen) {
    if (loading) return;

    if (!isOpen && action && onOpen) {
      onOpen()
    }

    if (isOpen && !action && onClose) {
      onClose()
    }

    setIsOpen(action);
  }

  const onSectionClick = function (e) {
    e?.preventDefault()

    toggleCollapse()
  }

  const onKeyDown = function (e) {
    if (e.keyCode === keyCodes.space || e.keyCode === keyCodes.enter) {
      e.preventDefault();
      e.stopPropagation();

      onSectionClick();
    }
  }

  const buttonAriaLabel = useMemo(function () {
    if (!aria?.labelLess || !aria?.labelMore) {
      return null
    }

    return isOpen ? aria?.labelLess : aria?.labelMore;
  }, [isOpen, aria])

  useEffect(() => {
    toggleCollapse(open)
  }, [open]);

  return (
    <div className={cx('root', className)} ref={ref}>
      <Tag className={cx('head', titleClassName)} tabIndex="0" onKeyDown={onKeyDown} onClick={onSectionClick}>
        {
          loading
          ? (<LoadingBlock theme="semi-rounded" style={{width: 200, height: 30}}/>)
          : title
        }
        <span className={cx('icon', {'open': isOpen})} aria-label={buttonAriaLabel}>
          <CurvedCircle />
        </span>
      </Tag>
      <div className={cx('body', {'open': isOpen})}>
        {isOpen && children}
      </div>
    </div>
  )
})

CollapsedSection.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  // title: PropTypes.string,
  titleClassName: PropTypes.string,
  aria: PropTypes.shape({
    labelMore: PropTypes.string,
    labelLess: PropTypes.string,
  }),
  loading: PropTypes.bool,
  tag: PropTypes.string,
}

CollapsedSection.defaultProps = {
  className: '',
  open: true,
  title: null,
  titleClassName: null,
  loading: false,
  aria: {},
}

export default CollapsedSection;
